// CellEditorController — the W5-D2 commit matrix owner. One instance per
// editable (popover) cell. The display span is the popover trigger; the
// popover body hosts the Track C adapter. The controller snapshots on
// popover:open, syncs the canonical hidden input from the atom's own
// change signals, and resolves the W5-D2 matrix: Enter commit+refocus,
// Tab/Shift+Tab commit+move (row wrap), outside-dismiss commit-when-dirty,
// Esc cancel (no commit event — `ui--work:editor-cancel` bubbles so the
// engine announces it), pointercancel/teardown cancel (no event at all).
import { Controller } from "@hotwired/stimulus";

export default class CellEditorController extends Controller {
  static values = {
    rowId: String,
    fieldKey: String,
    fieldType: String,
    prevValue: { type: String, default: "" },
    format: { type: String, default: "raw" },
  };

  static targets = ["display"];

  connect() {
    // Turbo-restoration safety (D1): teardown-then-rebind — a morph/restore
    // that reconnects the same element must not stack a second handler.
    this._teardown();
    this.editing = false;
    this.captureKeydown = (event) => this._handleKeydown(event, true);
    // Named instance refs: _teardown removes exactly what is bound here.
    this.onPopoverOpen = (e) => this._snapshot(e);
    this.onPopoverClose = (e) => this._onPopoverClose(e);
    this.onPointerCancel = () => {
      if (this.editing) this._cancel({ teardown: true });
    };
    // Capture phase on the editor root: wins over the popover's Tab trap
    // and any atom-internal handlers for the commit matrix keys.
    this.element.addEventListener("keydown", this.captureKeydown, true);
    this.element.addEventListener("popover:open", this.onPopoverOpen);
    this.element.addEventListener("popover:close", this.onPopoverClose);
    this.element.addEventListener("pointercancel", this.onPointerCancel);
    this._sync();
  }

  disconnect() {
    this._teardown();
  }

  // D1 teardown: symmetric removal of everything connect binds. The
  // popover/pointercancel handlers are the named refs above — their old
  // anonymous forms could never be removed and stacked on every reconnect.
  // Silent: teardown never commits; a live edit cancels without an event.
  _teardown() {
    if (this.editing) this._cancel({ teardown: true });
    if (this.captureKeydown) this.element.removeEventListener("keydown", this.captureKeydown, true);
    if (this.onPopoverOpen) this.element.removeEventListener("popover:open", this.onPopoverOpen);
    if (this.onPopoverClose) this.element.removeEventListener("popover:close", this.onPopoverClose);
    if (this.onPointerCancel) this.element.removeEventListener("pointercancel", this.onPointerCancel);
    this.captureKeydown = null;
    this.onPopoverOpen = null;
    this.onPopoverClose = null;
    this.onPointerCancel = null;
  }

  // === Open / close (grid-initiated or click) ===

  // Grid entry point (Enter/F2/printable prefill/Space-on-checkbox never
  // reaches here — the engine toggles those directly).
  openFromGrid({ prefill } = {}) {
    if (this.editing) return;
    const trigger = this.element.querySelector("button[data-ui--popover-target='trigger']");
    if (!trigger) return;
    this._pendingPrefill = prefill || null;
    trigger.click(); // popover opens via its own click action
  }

  closeToGrid() {
    const trigger = this.element.querySelector("button[data-ui--popover-target='trigger']");
    if (trigger && this.editing) trigger.click(); // dismissible popover hides on re-click
  }

  cancelFromGrid() {
    if (this.editing) this._cancel({});
  }

  _snapshot() {
    this.editing = true;
    this.displayTarget.dataset.workEditing = "true";
    this.displayTarget.classList.add("ring-1", "ring-primary");
    const canonical = this.element.querySelector("input[data-work-editor-value]");
    this.gestureToken = `e-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
    this.prevValue = canonical ? this._parse(canonical.value) : this._parse(this.prevValueValue);
    const body = this.element.querySelector("[data-work-editor-root]");
    const first = body && body.querySelector("input:not([type='hidden']), textarea, select, button");
    if (first) first.focus();
    if (this._pendingPrefill) this._applyPrefill(this._pendingPrefill);
    this._pendingPrefill = null;
  }

  _applyPrefill(char) {
    const body = this.element.querySelector("[data-work-editor-root]");
    const input = body && body.querySelector("input:not([type='hidden']), textarea");
    if (!input || input.type === "checkbox" || input.type === "radio" || input.type === "range") return;
    input.value = char;
    input.dispatchEvent(new Event("input", { bubbles: true }));
  }

  _sync() {
    const canonical = this.element.querySelector("input[data-work-editor-value]");
    if (!canonical) return;
    canonical.value = this._readAtom();
  }

  // Sync action (input/change/date-picker:change/combo-box:change
  // delegated at the editor root): pull the atom's value into the
  // canonical hidden input. Never reaches into atom internals beyond the
  // documented read targets.
  syncValue() {
    this._sync();
  }

  // Canonical value reader per adapter type (the well-known targets). The
  // number family (number/rating/slider) coerces to a canonical Number —
  // hydration carries JSON numbers, so both seam ends agree on the type.
  _readAtom() {
    const body = this.element.querySelector("[data-work-editor-root]");
    if (!body) return "";
    const hidden = (sel) => { const el = body.querySelector(sel); return el ? el.value : ""; };
    switch (this.fieldTypeValue) {
      case "number":
        return this._canonicalNumber(hidden("input[type='number']"));
      case "date":
        return hidden("input[data-ui--date-picker-target='hidden']");
      case "single_select":
        return hidden("input[data-ui--combo-box-target='hidden']");
      case "people":
        return hidden("input[data-ui--entity-selector-target='hidden']");
      case "multi_select": {
        const valuesHost = body.querySelector("[data-ui--multi-select-target='values']");
        const inputs = valuesHost ? valuesHost.querySelectorAll("input") : [];
        return JSON.stringify(Array.from(inputs).map((i) => i.value));
      }
      case "rating": {
        const checked = body.querySelector("input[type='radio']:checked");
        return this._canonicalNumber(checked ? checked.value : "");
      }
      case "slider":
        return this._canonicalNumber(hidden("input[type='range']"));
      default:
        return hidden("input[type='text']");
    }
  }

  // "" stays "" (no value yet); everything else becomes the Number the
  // hydration seam carries.
  _canonicalNumber(raw) {
    return raw === "" ? "" : Number(raw);
  }

  _parse(serialized) {
    if (this.formatValue === "json") {
      try { return JSON.parse(serialized); } catch { return null; }
    }
    // Symmetric with _readAtom/_serialize: the number family round-trips
    // the canonical input's string form into a Number (and "" stays "").
    if (["number", "rating", "slider"].includes(this.fieldTypeValue)) {
      return serialized === "" || serialized == null ? "" : Number(serialized);
    }
    return serialized;
  }

  // === The W5-D2 commit matrix (capture-phase keys win over popover) ===

  _handleKeydown(event) {
    if (!this.editing) return;
    // Multiline text: Enter is a newline, not a commit (W4 convention).
    if (event.key === "Enter" && event.target.tagName === "TEXTAREA") return;
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      this._commit({ refocus: true });
      this.closeToGrid();
      this._finishEditing({ refocus: true });
    } else if (event.key === "Tab") {
      event.preventDefault();
      event.stopPropagation();
      this._commit({ refocus: false });
      this.closeToGrid();
      this._finishEditing({ refocus: false });
      this._engineFocusAfter(event.shiftKey ? -1 : 1);
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      this.cancelFromGrid();
    }
  }

  _onPopoverClose() {
    if (!this.editing || this.canceling) return;
    // Outside-dismiss = commit-when-dirty (W5-D2). Clean dismiss: no event.
    this._commit({ refocus: false });
    this._finishEditing({ refocus: false });
  }

  // Enter: commit + refocus the cell; Tab: commit + move.
  _commit({ refocus }) {
    const canonical = this.element.querySelector("input[data-work-editor-value]");
    if (!canonical) return;
    const value = this._parse(canonical.value);
    if (this._isDirty(value)) {
      this.element.dispatchEvent(new CustomEvent("ui--work:editor-commit", {
        bubbles: true,
        detail: {
          row_id: this.rowIdValue,
          field_key: this.fieldKeyValue,
          value,
          gesture_token: this.gestureToken,
        },
      }));
      this.prevValue = value; // dirty check is now clean (popover:close re-commits nothing)
    }
  }

  _isDirty(value) {
    return JSON.stringify(value) !== JSON.stringify(this.prevValue);
  }

  _finishEditing({ refocus }) {
    this.editing = false;
    delete this.displayTarget.dataset.workEditing;
    this.displayTarget.classList.remove("ring-1", "ring-primary");
    if (refocus) this._refocusCell();
  }

  _refocusCell() {
    const td = this.element.closest("td[data-work-field]");
    (td || this.displayTarget).focus({ preventScroll: false });
  }

  _engineFocusAfter(delta) {
    const engine = this.element.closest("[data-controller~='ui--work-editable-table']");
    if (!engine) return;
    const td = this.element.closest("td[data-work-field]");
    const app = this.application;
    const ctrl = app && engine && app.getControllerForElementAndIdentifier(engine, "ui--work-editable-table");
    if (ctrl && td) ctrl.focusCellAfter(td, delta);
  }

  // Esc / pointercancel / teardown. No COMMIT event ever leaves (W5-D2):
  // Esc dispatches `ui--work:editor-cancel` (bubbles — the engine announces
  // the cancel), while teardown restores state without emitting anything.
  _cancel({ teardown } = {}) {
    this.canceling = true;
    this.closeToGrid();
    this.editing = false;
    if (!teardown) {
      const engine = this.element.closest("[data-controller~='ui--work-editable-table']");
      engine && engine.dispatchEvent(new CustomEvent("ui--work:editor-cancel", { bubbles: true }));
    }
    const canonical = this.element.querySelector("input[data-work-editor-value]");
    if (canonical) canonical.value = this._serialize(this.prevValue);
    delete this.displayTarget.dataset.workEditing;
    this.displayTarget.classList.remove("ring-1", "ring-primary");
    this.canceling = false;
  }

  _serialize(value) {
    return this.formatValue === "json" ? JSON.stringify(value ?? null) : String(value ?? "");
  }
}
